import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Stack,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { checkErrors, hasError } from "utils/forms-utils";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import { trafficViolationService } from "services/trafficViolationService";
import { ROUTES } from 'constants/routes-path';
import { maskCPF } from "utils/forms-utils";
import { validateCPF } from "utils/utils";

function TrafficViolationInformDriver() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const ids = useMemo(() => ({
        driverName: "nome_condutor",
        driverLicense: "cnh_indicacao_condutor",
        driverCpf: "documento_condutor",
        registryNumber: "numero_registro_indicacao_condutor",
        processNumber: "numero_processo_indicacao_condutor",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const initFormData = () => ({
        [ids.driverName]: { value: "", validations: [{ required: true }] },
        [ids.driverLicense]: { value: "", validations: [{ required: true }] },
        [ids.driverCpf]: { value: "", validations: [{ required: true }] },
        [ids.registryNumber]: { value: "", validations: [{ required: true }] },
        [ids.processNumber]: { value: "", validations: [{ required: true }] },
    });

    const [formData, setFormData] = useState();
    const [trafficViolation, setTrafficViolation] = useState();
    const [inputError, setInputError] = useState({});
    const [touched, setTouched] = useState({});

    const redirectToList = () => {
        navigate(ROUTES.trafficViolationsPath);
    };

    const fetchTrafficViolation = async () => {
        try {
            const trafficViolation = await trafficViolationService.details(id);
            if (!trafficViolation?.is_valid || !trafficViolation?.is_driver_informable) {
                showToast({
                    title: t("traffic-violation.messages.errors.find"),
                    status: "error",
                });
                redirectToList();
            }
            setTrafficViolation(trafficViolation);
        } catch (error) {
            showToast({
                title: t("traffic-violation.messages.errors.find"),
                status: "error",
            });
            redirectToList();
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        let formattedValue = value;

        if (id === ids.driverCpf) {
            formattedValue = maskCPF(value);
        } else if (id === ids.driverName || id === ids.processNumber) {
            formattedValue = value.toUpperCase();
        }

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: formattedValue },
        };

        let newErrors = checkErrors({ formData: newFormData, inputError, name: id });

        if (id === ids.driverCpf && formattedValue) {
            if (!validateCPF(formattedValue)) {
                newErrors = {
                    ...newErrors,
                    [ids.driverCpf]: "traffic-violation.messages.errors.cpf-format",
                };
            } else {
                const { [ids.driverCpf]: _, ...rest } = newErrors;
                newErrors = rest;
            }
        }

        setFormData(newFormData);
        setInputError(newErrors);
    };


    const handleBlur = (e) => {
        const id = e.target.id;
        setTouched((prev) => ({ ...prev, [id]: true }));
    };

    const toDTO = () => ({
        nome_condutor: formData[ids.driverName].value,
        cnh_indicacao_condutor: formData[ids.driverLicense].value,
        documento_condutor: formData[ids.driverCpf].value,
        numero_registro_indicacao_condutor: formData[ids.registryNumber].value,
        numero_processo_indicacao_condutor: formData[ids.processNumber].value,
    });

    const handleSubmit = async () => {
        if (hasError({ formData, inputError })) return;

        try {
            const data = toDTO();
            await trafficViolationService.updateDriver(id, data);

            showToast({
                title: t("traffic-violation.messages.success.inform-driver"),
                status: "success",
            });

            navigate(ROUTES.trafficViolationsPath);
        } catch (err) {
            showToast({
                title: t("traffic-violation.messages.errors.inform-driver"),
                status: "error",
            });
        }
    };

    const isFormInvalid = !formData || hasError({ formData, inputError });

    useEffect(() => {
        setFormData(initFormData());
        fetchTrafficViolation();
    }, []);

    return trafficViolation ? (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                <ListPageCardHeader title={t("traffic-violation-inform-driver.title")} subtitle={trafficViolation.numero_ait} />
                <CardBody px="22px">
                    {formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.driverName] && !!inputError[ids.driverName]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("traffic-violation-inform-driver.form.fields.driver-name.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.driverName}
                                        value={formData[ids.driverName].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("traffic-violation-inform-driver.form.fields.driver-name.placeholder")}
                                        autoFocus
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.driverName])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.driverLicense] && !!inputError[ids.driverLicense]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("traffic-violation-inform-driver.form.fields.driver-license.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.driverLicense}
                                        value={formData[ids.driverLicense].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("traffic-violation-inform-driver.form.fields.driver-license.placeholder")}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.driverLicense])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.driverCpf] && !!inputError[ids.driverCpf]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("traffic-violation-inform-driver.form.fields.driver-cpf.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.driverCpf}
                                        maxLength={14}
                                        value={formData[ids.driverCpf].value}
                                        onChange={(e) => {
                                            const masked = maskCPF(e.target.value);
                                            handleChange({ target: { id: ids.driverCpf, value: masked } });
                                        }}
                                        onBlur={handleBlur}
                                        placeholder={t("traffic-violation-inform-driver.form.fields.driver-cpf.placeholder")}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.driverCpf])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.registryNumber] && !!inputError[ids.registryNumber]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("traffic-violation-inform-driver.form.fields.registry-number.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.registryNumber}
                                        type="number"
                                        value={formData[ids.registryNumber].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("traffic-violation-inform-driver.form.fields.registry-number.placeholder")}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.registryNumber])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl
                                    isRequired
                                    isInvalid={touched[ids.processNumber] && !!inputError[ids.processNumber]}
                                >
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("traffic-violation-inform-driver.form.fields.process-number.label")}
                                    </FormLabel>
                                    <Input
                                        id={ids.processNumber}
                                        value={formData[ids.processNumber].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t("traffic-violation-inform-driver.form.fields.process-number.placeholder")}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.processNumber])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(`${ROUTES.trafficViolationsPath}`)}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "gray.500",
                                        color: "white"
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t("system.actions.save")}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    ) : (
        <Loading maxH={{ sm: "100px", md: "350px" }} />
    );
}

export default TrafficViolationInformDriver;